/*
  <bp-sticky-buy> (premium audit #12; T1 PDP #2 2026-10-21; taste audit 2026-10-24). Shows the bar once the main Add to cart button is
  scrolled out of view above, and hides it while the main button is on screen or still below. Its
  button clicks the main one, so the selected variant, quantity and Horizon's cart drawer behave exactly as the main
  form. Price, label, disabled state and the chosen variant name are copied from the main form whenever they change.
  While the bar shows, <html> gets .bp-sticky-on (bottom padding = bar height) so the last content is never covered;
  the bar sits above Shopify's cookie banner when that is open.
*/
class BpStickyBuy extends HTMLElement {
  connectedCallback() {
    this.bar = this.querySelector('.bp-sticky-buy__bar');
    this.btn = this.querySelector('[data-bp-sticky-btn]');
    this.priceEl = this.querySelector('[data-bp-sticky-price]');
    this.variantEl = this.querySelector('[data-bp-sticky-variant]');
    this.defaultLabel = this.btn?.textContent.trim();
    const details = this.closest('.product-details') || document;
    this.details = details;
    this.main = details.querySelector('button[name="add"]');
    this.mainPrice = details.querySelector('product-price .price, .price');
    if (!this.bar || !this.btn || !this.main || !('IntersectionObserver' in window)) return;
    this.controller = new AbortController();
    const { signal } = this.controller;

    this.btn.addEventListener('click', () => {
      if (this.main.disabled) return;
      this.main.click();
    }, { signal });

    // Taste audit 2026-10-24 (motion #4): only once the main button has scrolled ABOVE the view. On a phone's first
    // screen (button still below) the bar used to show at first paint and cover the Color label.
    this.io = new IntersectionObserver(([e]) => this.show(!e.isIntersecting && e.boundingClientRect.top < 0));
    this.io.observe(this.main);

    this.mo = new MutationObserver(() => this.sync());
    this.mo.observe(this.main, { attributes: true, attributeFilter: ['disabled'], childList: true, subtree: true, characterData: true });
    if (this.mainPrice) this.mo.observe(this.mainPrice, { childList: true, subtree: true, characterData: true });
    // chosen variant name follows the picker
    details.addEventListener('change', (e) => { if (e.target instanceof HTMLInputElement && e.target.closest('variant-picker')) this.sync(); }, { signal });
    addEventListener('resize', () => this.place(), { signal, passive: true });
  }

  disconnectedCallback() {
    this.io?.disconnect();
    this.mo?.disconnect();
    this.controller?.abort();
    document.documentElement.classList.remove('bp-sticky-on');
  }

  /** @param {boolean} on */
  show(on) {
    if (on) this.sync();
    this.bar.hidden = !on;
    document.documentElement.classList.toggle('bp-sticky-on', on);
    if (on) this.place();
  }

  // bottom padding for the page + lift the bar above Shopify's cookie banner when it is open
  place() {
    const banner = document.getElementById('shopify-pc__banner');
    // the banner is position: fixed, so offsetParent is always null: test its computed visibility and size instead
    const bs = banner && getComputedStyle(banner);
    const lift = bs && bs.display !== 'none' && bs.visibility !== 'hidden' ? banner.getBoundingClientRect().height : 0;
    this.bar.style.insetBlockEnd = lift ? `${Math.round(lift)}px` : '';
    document.documentElement.style.setProperty('--bp-sticky-h', `${Math.round(this.bar.getBoundingClientRect().height + lift)}px`);
  }

  sync() {
    if (this.mainPrice && this.priceEl) {
      const t = this.mainPrice.textContent.trim();
      if (t) this.priceEl.textContent = t;
    }
    if (this.variantEl) {
      // only options a shopper can change: skip hidden single-value groups (same rule as the Liquid)
      const chosen = [...this.details.querySelectorAll('variant-picker input:checked')].filter((i) => !i.closest('[data-bp-single-option]')).map((i) => i.value).filter(Boolean);
      if (chosen.length) this.variantEl.textContent = chosen.join(' / ');
    }
    this.btn.disabled = this.main.disabled;
    const label = this.main.querySelector('.add-to-cart-text')?.textContent.trim() || this.main.textContent.trim();
    this.btn.textContent = this.main.disabled && label ? label : this.defaultLabel;
  }
}

if (!customElements.get('bp-sticky-buy')) customElements.define('bp-sticky-buy', BpStickyBuy);
